/**
 * HTML ↔ düz metin köprüsü (059-b) — **bildirim şablonu içeriği** için.
 *
 * E-posta şablonlarının içeriği HTML'dir (zengin metin editörü), diğer kanallarınki (SMS/PN/Web) düz
 * metindir. Aynı kolon (`CONTENT_TR`/`CONTENT_EN`) iki biçimi de taşıdığından iki yönlü küçük bir
 * dönüşüm gerekir:
 *
 * - {@link looksLikeHtml} + {@link plainTextToHtml}: e-posta moduna geçilen bir şablonun **eski düz
 *   metni** editöre verilmeden önce yükseltilir. Aksi halde Quill, metni tek paragraf sayar ve
 *   satır sonları **sessizce kaybolur**.
 * - {@link hasVisibleText}: "boş mu?" sorusu HTML'de karakter sayısıyla cevaplanamaz — `<p><br></p>`
 *   veya `<p>&nbsp;</p>` doludur ama **görünür metni yoktur**.
 *
 * <p>Fonksiyonlar bilinçli olarak **DOM kullanmaz** (regex tabanlıdır): aynı mantık backend
 * `NotificationContentSanitizer` ile aynı yaklaşımı izler, testlerde jsdom'a bağımlı değildir ve
 * doğrulama yolunda (zod şeması) senkron çalışır.
 */

/**
 * En az bir **bilinen** HTML etiketi içeriyor mu?
 *
 * <p>Liste bilinçli olarak <b>kapalıdır</b> ("`<` + harf" gibi genel bir desen değil): şablon içeriği
 * düz metin de olabilir ve `<TBD>`, `<yer tutucu>` gibi meşru metinler genel desene uyuyordu. Bunun
 * iki somut sonucu vardı — böyle bir metin (1) "HTML" sanıldığı için editöre taşınırken satır sonları
 * <b>sessizce</b> tek paragrafa çöküyordu, (2) etiket sökme sonrası "boş" görünüp doğrulamada
 * reddediliyordu. Küme, Quill'in üretebildiği etiketleri ve eski içeriklerde görülebilecek temel
 * blokları kapsar.
 */
const HTML_TAG =
  /<\/?(?:p|br|hr|div|span|strong|b|em|i|u|s|del|ins|mark|small|a|img|ol|ul|li|dl|dt|dd|h[1-6]|blockquote|pre|code|table|thead|tbody|tfoot|tr|td|th|caption|sub|sup|font|center|iframe|video)\b[^>]*>/i

/** Herhangi bir etiket — sökme için. */
const ANY_TAG = /<[^>]*>/g

/** Metne çevrilirken **boşluk** üretmesi gereken varlıklar. */
const NBSP = /&nbsp;|&#160;|&#xa0;/gi

/** Ardışık boşluk/satır sonu. */
const WHITESPACE = /\s+/g

/**
 * Metne çevrilirken **satır sonu** üretmesi gereken etiketler — `htmlToDisplayText` için.
 *
 * <p>Küme `HTML_TAG`'in **blok** olanlarıdır; satır içi biçimler (`strong`, `em`, `a`, `span`…)
 * bilinçle dışarıdadır: onlar metnin ortasında durur ve satır kırsalardı cümle ikiye bölünürdü.
 * Hem açılış hem kapanış eşleşir — Quill `<p>a</p><p>b</p>` üretir, yalnız kapanışa bakmak son
 * paragraftan sonra fazladan bir satır bırakır, yalnız açılışa bakmak ilkinde eksik bırakırdı;
 * ikisini de alıp sonda fazla satırları sadeleştirmek daha az özel durum üretir.
 *
 * <p><b>`li` bilinçle YOK, `ul`/`ol` VAR:</b> madde satırını {@link LIST_ITEM_OPEN} zaten açıyor;
 * `li` burada da olsaydı `</li>` ikinci bir satır sonu üretir ve maddeler arasına <b>boş satır</b>
 * girerdi (test yakaladı). Listenin kendisi çevresindeki metinden `ul`/`ol` ile ayrılır.
 */
const BLOCK_BOUNDARY = /<\s*\/?\s*(?:br|p|div|ul|ol|tr|h[1-6]|blockquote|pre|hr)\b[^>]*>/gi

/** Liste maddesinin **açılışı** — madde imi üretir (bkz. `htmlToDisplayText`). */
const LIST_ITEM_OPEN = /<\s*li\b[^>]*>/gi

/** Satır **içi** boşluk (satır sonu HARİÇ) — `\s` kullanılamaz, `\n`'i de yerdi. */
const INLINE_SPACE = /[^\S\n]+/g

/** Satır sonunun etrafındaki boşluklar — satır başı/sonu boşluğu bırakmamak için. */
const LINE_EDGE_SPACE = /[^\S\n]*\n[^\S\n]*/g

/** Üç ve daha fazla ardışık satır sonu (boş paragraf yığınları). */
const EXTRA_BLANK_LINES = /\n{3,}/g

/**
 * Varlık (entity) çözme — {@link htmlToPlainText} ve {@link htmlToDisplayText} tek kaynaktan kullanır
 * ki listeleri ayrışmasın.
 *
 * <p><b>Çağıran, etiketleri ÖNCE sökmüş olmalıdır.</b> Ters sırada çözülen bir varlık yeni bir
 * "etiket" üretebilirdi (`&lt;script&gt;` → `<script>`) ve sökme onu kaçırırdı. (Backend
 * `NotificationContentSanitizer` ile aynı sıra.)
 */
function decodeEntities(value: string): string {
  return value
    .replace(NBSP, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&amp;/gi, '&')
}

/** `plainTextToHtml` içinde kaçırılacak karakterler. */
const ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
}

/**
 * Değer HTML mi? **Bilinen** bir etiket yoksa düz metin kabul edilir → `5 < 3`, `<TBD>` ve
 * `<yer tutucu>` düz metindir.
 */
export function looksLikeHtml(value: string | null | undefined): boolean {
  return !!value && HTML_TAG.test(value)
}

/**
 * Düz metni HTML'e yükseltir: özel karakterler kaçırılır, **her satır bir paragraf** olur ve boş
 * satırlar `<p><br></p>` ile korunur (Quill'in ürettiği biçimin aynısı → editör açıldığında değer
 * değişmiş görünmez).
 */
export function plainTextToHtml(text: string): string {
  const escaped = text.replace(/[&<>"]/g, (c) => ESCAPES[c])
  return escaped
    .split(/\r?\n/)
    .map((line) => (line.trim() === '' ? '<p><br></p>' : `<p>${line}</p>`))
    .join('')
}

/**
 * HTML'i (veya düz metni) görünür düz metne indirger: etiketler sökülür, `&nbsp;` boşluğa çevrilir,
 * kalan varlıklar çözülür ve boşluklar sadeleştirilir.
 *
 * <p>Varlık çözme **etiket sökmeden sonra** yapılır: ters sırada çözülen bir varlık yeni bir "etiket"
 * üretebilirdi (`&lt;script&gt;` → `<script>`) ve sökme onu kaçırırdı. (Backend
 * `NotificationContentSanitizer` ile aynı sıra.)
 */
export function htmlToPlainText(value: string | null | undefined): string {
  if (!value) return ''
  return decodeEntities(value.replace(ANY_TAG, ' ')).replace(WHITESPACE, ' ').trim()
}

/**
 * HTML'i **okunur** düz metne indirger — {@link htmlToPlainText}'in aksine **satır yapısını korur**.
 *
 * <p><b>Neden ayrı bir fonksiyon:</b> `htmlToPlainText` "boş mu / arama metni" sorularına hizmet eder
 * ve bu yüzden tüm boşlukları tek boşluğa çöktürür. Ekranda göstermek için o kayıp kabul edilemez:
 * `<p>Sayın Ali,</p><p>Görev tamamlandı.</p>` tek satıra yapışır ve okunmaz hâle gelir. İki ölçüt
 * gerçekten farklıdır, bu yüzden çöktürme davranışı parametreyle değil ayrı bir fonksiyonla
 * ayrılmıştır — varlık çözme ise {@link decodeEntities} ile **paylaşılır** ki listeler ayrışmasın.
 *
 * <p><b>HTML render EDİLMEZ.</b> Dönüş bir metindir ve çağıran onu metin düğümü olarak basar; hiçbir
 * yerde `dangerouslySetInnerHTML`/`innerHTML` yoktur, dolayısıyla XSS yüzeyi açılmaz. Bu, spec
 * 078/FR-011'in ("içerik HTML olarak yorumlanmaz") gereğidir — değişen tek şey, kullanıcının artık
 * ham etiket yerine etiketlerin **arasındaki metni** görmesidir (kullanıcı isteği 2026-08-28).
 *
 * <p><b>Kayıp bilinçlidir:</b> kalın/italik/bağlantı gibi biçimler düşer, bağlantının metni kalır ama
 * tıklanamaz. Şablon editörü (Quill) bunları üretebiliyor; sadakat isteniyorsa yol sandbox'lı bir
 * iframe'dir, o ayrı bir karardır.
 *
 * <p>Liste maddeleri `• ` ile başlar: `<li>a</li><li>b</li>` aksi hâlde iki çıplak satır olurdu ve
 * bunun bir liste olduğu kaybolurdu.
 */
export function htmlToDisplayText(value: string | null | undefined): string {
  if (!value) return ''
  const stripped = value
    // Madde imi: `BLOCK_BOUNDARY` artık `li`'yi kapsamıyor (bkz. orada), satır sonunu bu üretir.
    .replace(LIST_ITEM_OPEN, '\n• ')
    .replace(BLOCK_BOUNDARY, '\n')
    .replace(ANY_TAG, ' ')
  return decodeEntities(stripped)
    .replace(INLINE_SPACE, ' ')
    .replace(LINE_EDGE_SPACE, '\n')
    // Boş paragraf (`<p><br></p>`) yığınları en fazla BİR boş satıra iner: Quill her Enter'da bir
    // tane üretiyor, birebir korunsaydı içerik ekranda sayfalarca boşlukla açılırdı.
    .replace(EXTRA_BLANK_LINES, '\n\n')
    .trim()
}

/**
 * Değerin **görünür metni** var mı? Boş bir editörün ürettiği `<p><br></p>` gibi biçimsel-dolu ama
 * anlamsız içerikler `false` döner.
 *
 * <p>Dikkat: bu fonksiyon değeri **HTML kabul eder**. Düz metin bir içeriğe uygulanırsa etiket
 * sanılan parçalar sökülür — ör. `<TBD>` boş görünür. Biçimi bilmediğiniz bir içerik için
 * {@link hasContent} kullanın.
 */
export function hasVisibleText(value: string | null | undefined): boolean {
  return htmlToPlainText(value) !== ''
}

/**
 * Biçimden bağımsız "dolu mu?" ölçütü — **içerik alanları için doğru olan budur**, çünkü aynı alan
 * kanala göre HTML ya da düz metin taşır:
 *
 * <ul>
 *   <li><b>HTML</b> ise <b>görünür metin</b> aranır (`<p><br></p>` boştur),</li>
 *   <li><b>düz metin</b> ise yalnız boşluk olmaması yeterlidir — etiket sökme uygulanMAZ. Aksi halde
 *       `<TBD>` ya da `<yer tutucu>` gibi meşru bir düz metin sessizce "boş" sayılır ve kaydı
 *       reddedilir/düşürülürdü.</li>
 * </ul>
 */
export function hasContent(value: string | null | undefined): boolean {
  if (!value) return false
  return looksLikeHtml(value) ? hasVisibleText(value) : value.trim() !== ''
}
